import React, { useState, useEffect } from 'react';
import {
  Building, Home, MapPin, Ruler, BedDouble, Bath, Car, Compass,
  Shield, FileCheck, IndianRupee, Upload, Video, Image, Map,
  Check, ChevronDown, ChevronUp, Info, Sparkles, X, Plus,
  Building2, Trees, Warehouse, Store, Factory, Tractor
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

// Property Type Icons
const PROPERTY_ICONS = {
  apartment: Building,
  villa: Home,
  builder_floor: Building2,
  plot: Map,
  penthouse: Sparkles,
  studio: BedDouble,
  farmhouse: Trees,
  commercial_shop: Store,
  commercial_office: Building,
  commercial_land: Map,
  warehouse: Warehouse,
  industrial: Factory,
  agricultural: Tractor,
  pg_hostel: Building,
  co_working: Building2
};

const RealEstateListingForm = ({ 
  initialData = {}, 
  onSubmit, 
  onCancel,
  isEditing = false 
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    property: true,
    area: true,
    amenities: false,
    legal: false,
    location: false,
    media: false
  });

  const [formData, setFormData] = useState({
    // Basic
    title: '',
    description: '',
    listing_type: 'sell',
    price: '',
    price_negotiable: false,
    
    // Property Type
    property_type: 'apartment',
    
    // Property Details
    bedrooms: '',
    bathrooms: '',
    balconies: '',
    floor_number: '',
    total_floors: '',
    facing: '',
    age_of_property: '',
    possession_status: 'ready_to_move',
    possession_date: '',
    furnishing: 'unfurnished',
    parking: '',
    parking_count: '',
    
    // Area
    carpet_area: '',
    built_up_area: '',
    super_built_up_area: '',
    plot_area: '',
    area_unit: 'sqft',
    
    // Amenities
    amenities: [],
    
    // Legal
    ownership_type: 'freehold',
    rera_registered: false,
    rera_id: '',
    approved_by: '',
    
    // Pricing
    price_per_sqft: '',
    maintenance_monthly: '',
    booking_amount: '',
    
    // Location
    location_city: '',
    location_state: '',
    full_address: '',
    landmark: '',
    pincode: '',
    latitude: '',
    longitude: '',
    google_maps_link: '',
    
    // Media
    images: [],
    video_url: '',
    virtual_tour_url: '',
    floor_plan_images: [],
    
    // Contact
    contact_phone: '',
    contact_whatsapp: '',
    contact_email: '',
    
    ...initialData
  });

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const response = await api.get('/p2p/real-estate/options');
      setOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch options:', error);
      toast.error('Failed to load form options');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate price per sqft
    if ((field === 'price' || field === 'carpet_area') && formData.carpet_area && formData.price) {
      const price = field === 'price' ? value : formData.price;
      const area = field === 'carpet_area' ? value : formData.carpet_area;
      if (price && area && area > 0) {
        setFormData(prev => ({ 
          ...prev, 
          [field]: value,
          price_per_sqft: Math.round(price / area) 
        }));
      }
    }
  };

  const toggleAmenity = (amenityId) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenityId)
        ? prev.amenities.filter(a => a !== amenityId)
        : [...prev.amenities, amenityId]
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title) {
      toast.error('Please enter property title');
      return;
    }
    if (!formData.price) {
      toast.error('Please enter property price');
      return;
    }
    if (!formData.location_city || !formData.location_state) {
      toast.error('Please enter location details');
      return;
    }

    setSaving(true);
    try {
      await onSubmit(formData);
      toast.success(isEditing ? 'Listing updated!' : 'Property listed successfully!');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to save listing');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const SectionHeader = ({ title, icon: Icon, section, badge }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <span className="font-semibold">{title}</span>
        {badge && (
          <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">
            {badge}
          </span>
        )}
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="w-5 h-5 text-muted-foreground" />
      ) : (
        <ChevronDown className="w-5 h-5 text-muted-foreground" />
      )}
    </button>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Property Type Selection */}
      <div className="glass-card p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Building className="w-5 h-5 text-primary" />
          Property Type
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {options?.property_types?.map(type => {
            const Icon = PROPERTY_ICONS[type.id] || Building;
            const isSelected = formData.property_type === type.id;
            return (
              <button
                key={type.id}
                type="button"
                onClick={() => handleChange('property_type', type.id)}
                className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  isSelected 
                    ? 'border-primary bg-primary/10' 
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <Icon className={`w-6 h-6 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className="text-xs text-center">{type.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Basic Information */}
      <div className="glass-card overflow-hidden">
        <SectionHeader title="Basic Information" icon={Info} section="basic" />
        {expandedSections.basic && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Property Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={e => handleChange('title', e.target.value)}
                placeholder="e.g., 3 BHK Luxury Apartment in Bandra West"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={e => handleChange('description', e.target.value)}
                placeholder="Describe your property in detail..."
                rows={4}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none resize-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Listing Type</label>
                <select
                  value={formData.listing_type}
                  onChange={e => handleChange('listing_type', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                >
                  <option value="sell">For Sale</option>
                  <option value="rent">For Rent</option>
                  <option value="lease">For Lease</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Price (₹) *</label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={e => handleChange('price', e.target.value)}
                  placeholder="Enter price"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                  required
                />
              </div>

              <div className="flex items-center gap-3 pt-8">
                <input
                  type="checkbox"
                  id="price_negotiable"
                  checked={formData.price_negotiable}
                  onChange={e => handleChange('price_negotiable', e.target.checked)}
                  className="w-5 h-5 rounded"
                />
                <label htmlFor="price_negotiable" className="text-sm">Price Negotiable</label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Property Details */}
      <div className="glass-card overflow-hidden">
        <SectionHeader title="Property Details" icon={Home} section="property" />
        {expandedSections.property && (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  <BedDouble className="w-4 h-4 inline mr-1" /> Bedrooms (BHK)
                </label>
                <select
                  value={formData.bedrooms}
                  onChange={e => handleChange('bedrooms', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                >
                  <option value="">Select</option>
                  {options?.bhk_options?.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  <Bath className="w-4 h-4 inline mr-1" /> Bathrooms
                </label>
                <select
                  value={formData.bathrooms}
                  onChange={e => handleChange('bathrooms', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                >
                  <option value="">Select</option>
                  {[1, 2, 3, 4, 5, 6].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Balconies</label>
                <select
                  value={formData.balconies}
                  onChange={e => handleChange('balconies', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                >
                  <option value="">Select</option>
                  {[0, 1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  <Compass className="w-4 h-4 inline mr-1" /> Facing
                </label>
                <select
                  value={formData.facing}
                  onChange={e => handleChange('facing', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                >
                  <option value="">Select</option>
                  {options?.facing_options?.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Floor Number</label>
                <input
                  type="number"
                  value={formData.floor_number}
                  onChange={e => handleChange('floor_number', e.target.value)}
                  placeholder="e.g., 5"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Total Floors</label>
                <input
                  type="number"
                  value={formData.total_floors}
                  onChange={e => handleChange('total_floors', e.target.value)}
                  placeholder="e.g., 20"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Property Age</label>
                <select
                  value={formData.age_of_property}
                  onChange={e => handleChange('age_of_property', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                >
                  <option value="">Select</option>
                  {options?.age_options?.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Furnishing</label>
                <select
                  value={formData.furnishing}
                  onChange={e => handleChange('furnishing', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                >
                  {options?.furnishing_options?.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Possession Status</label>
                <select
                  value={formData.possession_status}
                  onChange={e => handleChange('possession_status', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                >
                  {options?.possession_status?.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
              </div>

              {formData.possession_status === 'under_construction' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Possession Date</label>
                  <input
                    type="date"
                    value={formData.possession_date}
                    onChange={e => handleChange('possession_date', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">
                  <Car className="w-4 h-4 inline mr-1" /> Parking
                </label>
                <select
                  value={formData.parking}
                  onChange={e => handleChange('parking', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                >
                  <option value="">Select</option>
                  <option value="covered">Covered</option>
                  <option value="open">Open</option>
                  <option value="both">Both</option>
                  <option value="none">None</option>
                </select>
              </div>

              {formData.parking && formData.parking !== 'none' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Parking Count</label>
                  <input
                    type="number"
                    value={formData.parking_count}
                    onChange={e => handleChange('parking_count', e.target.value)}
                    placeholder="e.g., 2"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Area & Dimensions */}
      <div className="glass-card overflow-hidden">
        <SectionHeader title="Area & Dimensions" icon={Ruler} section="area" />
        {expandedSections.area && (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Carpet Area</label>
                <input
                  type="number"
                  value={formData.carpet_area}
                  onChange={e => handleChange('carpet_area', e.target.value)}
                  placeholder="e.g., 1200"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Built-up Area</label>
                <input
                  type="number"
                  value={formData.built_up_area}
                  onChange={e => handleChange('built_up_area', e.target.value)}
                  placeholder="e.g., 1400"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Super Built-up</label>
                <input
                  type="number"
                  value={formData.super_built_up_area}
                  onChange={e => handleChange('super_built_up_area', e.target.value)}
                  placeholder="e.g., 1600"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Plot Area</label>
                <input
                  type="number"
                  value={formData.plot_area}
                  onChange={e => handleChange('plot_area', e.target.value)}
                  placeholder="For plots/villas"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Unit</label>
                <select
                  value={formData.area_unit}
                  onChange={e => handleChange('area_unit', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                >
                  {options?.area_units?.map(unit => (
                    <option key={unit.id} value={unit.id}>{unit.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {formData.carpet_area && formData.price && (
              <div className="p-4 bg-primary/10 rounded-xl">
                <p className="text-sm">
                  <IndianRupee className="w-4 h-4 inline mr-1" />
                  Price per {formData.area_unit}: <strong>₹{formData.price_per_sqft?.toLocaleString() || Math.round(formData.price / formData.carpet_area).toLocaleString()}</strong>
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Amenities */}
      <div className="glass-card overflow-hidden">
        <SectionHeader 
          title="Amenities & Features" 
          icon={Sparkles} 
          section="amenities" 
          badge={formData.amenities.length > 0 ? `${formData.amenities.length} selected` : null}
        />
        {expandedSections.amenities && (
          <div className="p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {options?.amenities?.map(amenity => {
                const isSelected = formData.amenities.includes(amenity.id);
                return (
                  <button
                    key={amenity.id}
                    type="button"
                    onClick={() => toggleAmenity(amenity.id)}
                    className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 text-sm ${
                      isSelected 
                        ? 'border-green-500 bg-green-500/10 text-green-500' 
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    {isSelected ? (
                      <Check className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <Plus className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                    )}
                    <span className="truncate">{amenity.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Legal & Documentation */}
      <div className="glass-card overflow-hidden">
        <SectionHeader title="Legal & Documentation" icon={FileCheck} section="legal" />
        {expandedSections.legal && (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Ownership Type</label>
                <select
                  value={formData.ownership_type}
                  onChange={e => handleChange('ownership_type', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                >
                  {options?.ownership_types?.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Approved By</label>
                <input
                  type="text"
                  value={formData.approved_by}
                  onChange={e => handleChange('approved_by', e.target.value)}
                  placeholder="e.g., HUDA, DDA, DTCP"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                />
              </div>

              <div className="flex items-center gap-3 pt-8">
                <input
                  type="checkbox"
                  id="rera_registered"
                  checked={formData.rera_registered}
                  onChange={e => handleChange('rera_registered', e.target.checked)}
                  className="w-5 h-5 rounded"
                />
                <label htmlFor="rera_registered" className="text-sm">RERA Registered</label>
              </div>
            </div>

            {formData.rera_registered && (
              <div>
                <label className="block text-sm font-medium mb-2">RERA ID</label>
                <input
                  type="text"
                  value={formData.rera_id}
                  onChange={e => handleChange('rera_id', e.target.value)}
                  placeholder="Enter RERA registration number"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Maintenance (₹/month)</label>
                <input
                  type="number"
                  value={formData.maintenance_monthly}
                  onChange={e => handleChange('maintenance_monthly', e.target.value)}
                  placeholder="e.g., 5000"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Booking Amount (₹)</label>
                <input
                  type="number"
                  value={formData.booking_amount}
                  onChange={e => handleChange('booking_amount', e.target.value)}
                  placeholder="e.g., 100000"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Location */}
      <div className="glass-card overflow-hidden">
        <SectionHeader title="Location Details" icon={MapPin} section="location" />
        {expandedSections.location && (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">City *</label>
                <input
                  type="text"
                  value={formData.location_city}
                  onChange={e => handleChange('location_city', e.target.value)}
                  placeholder="e.g., Mumbai"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">State *</label>
                <input
                  type="text"
                  value={formData.location_state}
                  onChange={e => handleChange('location_state', e.target.value)}
                  placeholder="e.g., Maharashtra"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Pincode</label>
                <input
                  type="text"
                  value={formData.pincode}
                  onChange={e => handleChange('pincode', e.target.value)}
                  placeholder="e.g., 400050"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Full Address</label>
              <textarea
                value={formData.full_address}
                onChange={e => handleChange('full_address', e.target.value)}
                placeholder="Enter complete address with building name, street, area..."
                rows={2}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none resize-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Landmark</label>
                <input
                  type="text"
                  value={formData.landmark}
                  onChange={e => handleChange('landmark', e.target.value)}
                  placeholder="e.g., Near Phoenix Mall"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  <Map className="w-4 h-4 inline mr-1" /> Google Maps Link
                </label>
                <input
                  type="url"
                  value={formData.google_maps_link}
                  onChange={e => handleChange('google_maps_link', e.target.value)}
                  placeholder="Paste Google Maps link"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Latitude</label>
                <input
                  type="text"
                  value={formData.latitude}
                  onChange={e => handleChange('latitude', e.target.value)}
                  placeholder="e.g., 19.0760"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Longitude</label>
                <input
                  type="text"
                  value={formData.longitude}
                  onChange={e => handleChange('longitude', e.target.value)}
                  placeholder="e.g., 72.8777"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Media & Virtual Tour */}
      <div className="glass-card overflow-hidden">
        <SectionHeader title="Photos, Video & Virtual Tour" icon={Image} section="media" />
        {expandedSections.media && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                <Video className="w-4 h-4 inline mr-1" /> Property Video URL
              </label>
              <input
                type="url"
                value={formData.video_url}
                onChange={e => handleChange('video_url', e.target.value)}
                placeholder="YouTube or direct video link"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                <Sparkles className="w-4 h-4 inline mr-1" /> Virtual Tour URL
              </label>
              <input
                type="url"
                value={formData.virtual_tour_url}
                onChange={e => handleChange('virtual_tour_url', e.target.value)}
                placeholder="360° virtual tour link (Matterport, etc.)"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
              />
            </div>

            <div className="p-4 border-2 border-dashed border-white/20 rounded-xl text-center">
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Property images and floor plans can be uploaded after creating the listing
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Contact Information */}
      <div className="glass-card p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          Contact Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Phone Number</label>
            <input
              type="tel"
              value={formData.contact_phone}
              onChange={e => handleChange('contact_phone', e.target.value)}
              placeholder="+91 98765 43210"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">WhatsApp</label>
            <input
              type="tel"
              value={formData.contact_whatsapp}
              onChange={e => handleChange('contact_whatsapp', e.target.value)}
              placeholder="+91 98765 43210"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={e => handleChange('contact_email', e.target.value)}
              placeholder="contact@example.com"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary outline-none"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-medium"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={saving}
          className="px-8 py-3 bg-primary text-primary-foreground rounded-xl font-semibold flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? (
            <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
          ) : (
            <Check className="w-5 h-5" />
          )}
          {isEditing ? 'Update Listing' : 'Create Listing'}
        </button>
      </div>
    </form>
  );
};

export default RealEstateListingForm;
